import { Component, OnInit } from '@angular/core';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { WaiterService } from '../../../../_service/waiter.service';

interface OrderItem {
  id: number;
  inventory: string;
  image: string;
  quantity: number;
  amount: string;
  originalQuantity: number;
  returned: boolean;
  return_reason?: string;
}

interface Order {
  id: number;
  uid: string;
  order_items: OrderItem[];
  [key: string]: any;
}

@Component({
  selector: 'app-select-items',
  standalone: true,
  imports: [RouterModule, CommonModule, ReactiveFormsModule],
  templateUrl: './select-items.component.html',
  styleUrl: './select-items.component.scss'
})
export class SelectItemsComponent implements OnInit {
  order: Order | null = null;
  returnForm: FormGroup;
  returnItems: OrderItem[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private waiterService: WaiterService,
    private fb: FormBuilder
  ) {
    this.returnForm = this.fb.group({
      return_reason: ['', Validators.required]
    });
  }

  ngOnInit() {
    const orderId = this.route.snapshot.paramMap.get('id');
    if (orderId) {
      this.loadOrderDetails(orderId);
    }
  }

  loadOrderDetails(orderId: string) {
    this.waiterService.getOrderDetails(orderId).subscribe({
      next: (response: Order) => {
        this.order = response;
        // Initialize returnItems with original quantities
        this.returnItems = response.order_items.map(item => ({
          ...item,
          originalQuantity: item.quantity,
          quantity: item.quantity
        }));
      },
      error: (error) => {
        console.error('Error loading order:', error);
        Swal.fire('Error', 'Failed to load order details', 'error');
      }
    });
  }

  decreaseQuantity(item: OrderItem) {
    if (item.quantity > 0) {
      item.quantity--;
    }
  }

  canSubmit(): boolean {
    return this.returnItems.some(item => item.quantity < item.originalQuantity)
      && this.returnForm.valid;
  }

  confirm() {
    if (!this.canSubmit()) {
      Swal.fire('Error', 'Please select items to return and provide a reason', 'error');
      return;
    }

    const returnData = {
      order_id: this.order?.uid,
      return_reason: this.returnForm.get('return_reason')?.value,
      return_type:'single',
      order_items: this.returnItems
        .filter(item => item.quantity < item.originalQuantity)
        .map(item => ({
          id: item.id,
          quantity: item.originalQuantity - item.quantity // Return quantity is the difference
        }))
    };

    Swal.fire({
      title: 'Confirm Return',
      text: 'Are you sure you want to process these returns?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, process return',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.processReturn(returnData);
      }
    });
  }

  private processReturn(returnData: any) {
    console.log(returnData);
    this.waiterService.processReturn(returnData).subscribe({
      next: (response) => {
        Swal.fire({
          title: 'Success',
          text: 'Return has been processed successfully',
          icon: 'success',
          confirmButtonText: 'Done'
        }).then(() => {
          this.router.navigate([`/orders/${this.order?.uid}`]);
        });
      },
      error: (error) => {
        console.error('Return processing error:', error);
        Swal.fire('Error', error.error?.message || 'Failed to process return', 'error');
      }
    });
  }
}
